import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Send, ImagePlus, X, RefreshCw, Mail, MailOpen } from 'lucide-react';

const AUDIENCE_LABELS = {
  all: 'All subscribers',
  free: 'Free subscribers only',
  paid: 'Paid supporters only',
};

const LIST_LABELS = {
  site: 'Site subscribers (new list)',
  legacy: 'Legacy list (old list)',
  beach_bum_society: 'Beach Bum Society',
  customers: 'Customers (anyone who has ordered)',
  all: 'Everyone (every list)',
};

// Lists that are their own standalone group — no free/paid supporter
// sub-filter applies, matching the backend's TAGGED_SUBSCRIBER_SOURCES.
const TAGGED_LIST_TARGETS = ['legacy', 'beach_bum_society', 'customers'];

// ─── Broadcast Tab ───────────────────────────────────────────────────────────

function BroadcastTab() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('all');
  const [listTarget, setListTarget] = useState('site');
  const [excludeCalendarPurchasers, setExcludeCalendarPurchasers] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [insertingImage, setInsertingImage] = useState(false);
  const fileInputRef = useRef(null);
  const inlineImageInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large. Maximum size: 10MB'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post('/upload/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.url) { setImageUrl(response.data.url); toast.success('Image uploaded'); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleBodyChange = (e) => {
    setBody(e.target.value);
    setCursorPosition(e.target.selectionStart);
  };

  const handleInsertImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large. Maximum size: 10MB'); return; }
    setInsertingImage(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post('/upload/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.url) {
        const beforeCursor = body.substring(0, cursorPosition);
        const afterCursor = body.substring(cursorPosition);
        const imageMarkdown = `\n\n![](${response.data.url})\n\n`;
        const newBody = beforeCursor + imageMarkdown + afterCursor;
        setBody(newBody);
        setCursorPosition(cursorPosition + imageMarkdown.length);
        toast.success('Image uploaded and inserted!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setInsertingImage(false);
      e.target.value = '';
    }
  };

  const handleSendClick = () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message body are required'); return; }
    setShowConfirm(true);
  };

  const confirmSend = async () => {
    setSending(true);
    setShowConfirm(false);
    try {
      const response = await axios.post('/newsletters/broadcast', {
        subject: subject.trim(), body: body.trim(), audience, list_target: listTarget, image_url: imageUrl || null,
        exclude_calendar_purchasers: excludeCalendarPurchasers,
      });
      toast.success(response.data.message);
      setSubject(''); setBody(''); setAudience('all'); setListTarget('site'); setImageUrl(''); setExcludeCalendarPurchasers(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message body are required'); return; }
    if (!testEmail.trim()) { toast.error('Enter an email address to send the test to'); return; }
    setSendingTest(true);
    try {
      const response = await axios.post('/newsletters/broadcast/test', {
        subject: subject.trim(), body: body.trim(), image_url: imageUrl || null, test_email: testEmail.trim(),
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="form-group">
          <label htmlFor="broadcast-subject">Subject</label>
          <input type="text" id="broadcast-subject" className="form-input" value={subject}
            onChange={(e) => setSubject(e.target.value)} placeholder="Email subject line" />
        </div>

        <div className="form-group">
          <label>Header Image <span style={{ color: '#999', fontWeight: 'normal' }}>(optional — one image, always appears above the message. To add more images within the message itself, use "Insert Image" below.)</span></label>
          {imageUrl ? (
            <div style={{ position: 'relative', display: 'inline-block', marginTop: '8px' }}>
              <img src={imageUrl} alt="Broadcast" style={{ maxWidth: '100%', maxHeight: '260px', borderRadius: '6px', display: 'block' }} />
              <button onClick={() => setImageUrl('')}
                style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
                <X size={14} />
              </button>
            </div>
          ) : (
            <>
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />
              <div>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <ImagePlus size={16} />
                  {uploading ? 'Uploading...' : 'Add Image'}
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label htmlFor="broadcast-body" style={{ margin: 0 }}>Message</label>
            <input type="file" accept="image/*" ref={inlineImageInputRef} onChange={handleInsertImage} style={{ display: 'none' }} />
            <Button type="button" variant="outline" size="sm" onClick={() => inlineImageInputRef.current?.click()} disabled={insertingImage}>
              <ImagePlus size={16} style={{ marginRight: '6px' }} />
              {insertingImage ? 'Uploading...' : 'Insert Image'}
            </Button>
          </div>
          <textarea id="broadcast-body" className="form-input" value={body} onChange={handleBodyChange}
            onSelect={(e) => setCursorPosition(e.target.selectionStart)}
            placeholder="Write your message here. Markdown formatting is supported.&#10;&#10;To add images anywhere in the message, click 'Insert Image' — it'll drop in at your cursor position."
            rows={14}
            style={{ resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        <div className="form-group">
          <label htmlFor="broadcast-list">List</label>
          <select id="broadcast-list" className="form-input" value={listTarget} onChange={(e) => setListTarget(e.target.value)}>
            <option value="site">Site subscribers (new list)</option>
            <option value="legacy">Legacy list (old list)</option>
            <option value="beach_bum_society">Beach Bum Society</option>
            <option value="customers">Customers (anyone who has ordered)</option>
            <option value="all">Everyone (every list)</option>
          </select>
        </div>

        {!TAGGED_LIST_TARGETS.includes(listTarget) && (
          <div className="form-group">
            <label htmlFor="broadcast-audience">Subscriber type</label>
            <select id="broadcast-audience" className="form-input" value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="all">All subscribers</option>
              <option value="free">Free subscribers only</option>
              <option value="paid">Paid supporters only</option>
            </select>
          </div>
        )}

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 'normal' }}>
            <input type="checkbox" checked={excludeCalendarPurchasers}
              onChange={(e) => setExcludeCalendarPurchasers(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
            <span>
              Skip people who've already bought a calendar
              <span style={{ display: 'block', fontSize: '13px', color: '#666', fontWeight: 'normal' }}>
                Useful for a pre-order/sale announcement — won't nag someone who already ordered.
              </span>
            </span>
          </label>
        </div>

        <div style={{ margin: '20px 0', paddingTop: '20px', borderTop: '1px solid #eee' }}>
          <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#666' }}>
            Want to see it first? Send yourself a preview — it won't count against the audience above or block the real send.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="email" className="form-input" placeholder="you@example.com" value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)} style={{ flex: 1 }} />
            <Button variant="outline" onClick={sendTest} disabled={sendingTest}>
              {sendingTest ? 'Sending...' : 'Send Test'}
            </Button>
          </div>
        </div>

        <Button onClick={handleSendClick} disabled={sending || !subject.trim() || !body.trim()}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Send size={16} />
          {sending ? 'Sending...' : 'Send Broadcast'}
        </Button>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Broadcast</DialogTitle></DialogHeader>
          <div style={{ padding: '8px 0 16px' }}>
            <p style={{ marginBottom: '8px' }}>
              You're about to send <strong>"{subject}"</strong> to the{' '}
              <strong>{LIST_LABELS[listTarget].toLowerCase()}</strong>
              {!TAGGED_LIST_TARGETS.includes(listTarget) && <> — <strong>{AUDIENCE_LABELS[audience].toLowerCase()}</strong></>}
              {excludeCalendarPurchasers && <>, skipping anyone who's already bought a calendar</>}.
            </p>
            <p style={{ color: '#666', fontSize: '14px' }}>This cannot be undone.</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button onClick={confirmSend} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Send size={16} /> Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Weekly Digest Tab ────────────────────────────────────────────────────────

function defaultSubject() {
  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'long' });
  return `This Week from the OBX Beach Bum — ${month}`;
}

function DigestTab() {
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState(defaultSubject());
  const [audience, setAudience] = useState('all');
  const [listTarget, setListTarget] = useState('site');
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const resp = await axios.get('/newsletters/digest/preview?days=7');
      setPosts(resp.data);
    } catch {
      toast.error('Failed to load posts preview');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []);

  const confirmSend = async () => {
    setSending(true);
    setShowConfirm(false);
    try {
      const resp = await axios.post('/newsletters/digest', {
        subject: subject.trim(),
        audience,
        list_target: listTarget,
        days: 7,
      });
      toast.success(resp.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!testEmail.trim()) { toast.error('Enter an email address to send the test to'); return; }
    setSendingTest(true);
    try {
      const resp = await axios.post('/newsletters/digest/test', {
        subject: subject.trim(), days: 7, test_email: testEmail.trim(),
      });
      toast.success(resp.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <>
      <div className="card">
        <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
          Sends a digest of all posts published in the last 7 days. The email shows each post's image, title, excerpt, and a link back to the site.
        </p>

        {/* Posts preview */}
        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <label style={{ margin: 0 }}>Posts included ({loading ? '…' : posts?.length ?? 0})</label>
            <button onClick={loadPreview} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }} title="Refresh">
              <RefreshCw size={14} />
            </button>
          </div>

          {loading ? (
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>Loading…</p>
          ) : posts?.length === 0 ? (
            <p style={{ color: '#ef4444', fontSize: '14px' }}>No published posts in the last 7 days.</p>
          ) : (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              {posts.map((post, i) => (
                <div key={post.id} style={{
                  display: 'flex', gap: '12px', padding: '12px 14px', alignItems: 'center',
                  borderBottom: i < posts.length - 1 ? '1px solid #f3f4f6' : 'none',
                  background: 'white',
                }}>
                  {post.image_url && (
                    <img src={post.image_url} alt="" style={{ width: '60px', height: '42px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                  )}
                  <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{post.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Subject */}
        <div className="form-group">
          <label htmlFor="digest-subject">Subject</label>
          <input type="text" id="digest-subject" className="form-input" value={subject}
            onChange={(e) => setSubject(e.target.value)} placeholder="Email subject line" />
        </div>

        {/* List */}
        <div className="form-group">
          <label htmlFor="digest-list">List</label>
          <select id="digest-list" className="form-input" value={listTarget} onChange={(e) => setListTarget(e.target.value)}>
            <option value="site">Site subscribers (new list)</option>
            <option value="legacy">Legacy list (old list)</option>
            <option value="beach_bum_society">Beach Bum Society</option>
            <option value="customers">Customers (anyone who has ordered)</option>
            <option value="all">Everyone (every list)</option>
          </select>
        </div>

        {!TAGGED_LIST_TARGETS.includes(listTarget) && (
          <div className="form-group">
            <label htmlFor="digest-audience">Subscriber type</label>
            <select id="digest-audience" className="form-input" value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="all">All subscribers</option>
              <option value="free">Free subscribers only</option>
              <option value="paid">Paid supporters only</option>
            </select>
          </div>
        )}

        <div style={{ margin: '20px 0', paddingTop: '20px', borderTop: '1px solid #eee' }}>
          <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#666' }}>
            Want to see it first? Send yourself a preview — it won't count against the audience above or block the real send.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="email" className="form-input" placeholder="you@example.com" value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)} style={{ flex: 1 }} />
            <Button variant="outline" onClick={sendTest} disabled={sendingTest || !posts?.length}>
              {sendingTest ? 'Sending...' : 'Send Test'}
            </Button>
          </div>
        </div>

        <Button
          onClick={() => setShowConfirm(true)}
          disabled={sending || !posts?.length || !subject.trim()}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Send size={16} />
          {sending ? 'Sending...' : 'Send Weekly Digest'}
        </Button>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Weekly Digest</DialogTitle></DialogHeader>
          <div style={{ padding: '8px 0 16px' }}>
            <p style={{ marginBottom: '8px' }}>
              You're about to send <strong>"{subject}"</strong> ({posts?.length} post{posts?.length !== 1 ? 's' : ''}) to the{' '}
              <strong>{LIST_LABELS[listTarget].toLowerCase()}</strong>
              {!TAGGED_LIST_TARGETS.includes(listTarget) && <> — <strong>{AUDIENCE_LABELS[audience].toLowerCase()}</strong></>}.
            </p>
            <p style={{ color: '#666', fontSize: '14px' }}>This cannot be undone.</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button onClick={confirmSend} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Send size={16} /> Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Sent History Tab ─────────────────────────────────────────────────────────

function ResendDialog({ broadcast, onClose, onSent }) {
  const nonOpeners = broadcast.sent - broadcast.opened;
  const [subject, setSubject] = useState(
    broadcast.subject.startsWith('Reminder: ') ? broadcast.subject : `Reminder: ${broadcast.subject}`
  );
  const [body, setBody] = useState(broadcast.body);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message body are required'); return; }
    setSending(true);
    try {
      const resp = await axios.post(`/admin/broadcasts/${broadcast.id}/resend-non-openers`, {
        subject: subject.trim(), body: body.trim(), image_url: broadcast.image_url || null,
      });
      toast.success(resp.data.message);
      onSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: '560px' }}>
        <DialogHeader><DialogTitle>Resend to Non-Openers</DialogTitle></DialogHeader>
        <div style={{ padding: '8px 0 16px' }}>
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
            Sends to the <strong>{nonOpeners}</strong> {nonOpeners === 1 ? 'person who' : 'people who'} didn't open
            "{broadcast.subject}". Give it a fresh subject line — same subject/body rarely does better the second time.
          </p>
          <div className="form-group">
            <label>Subject</label>
            <input type="text" className="form-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Message</label>
            <textarea className="form-input" value={body} onChange={(e) => setBody(e.target.value)} rows={10}
              style={{ resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Send size={16} /> {sending ? 'Sending...' : `Resend to ${nonOpeners}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryTab() {
  const [broadcasts, setBroadcasts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendTarget, setResendTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await axios.get('/admin/broadcasts');
      setBroadcasts(resp.data);
    } catch (error) {
      toast.error('Failed to load broadcast history');
      setBroadcasts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <p style={{ color: '#9ca3af', fontSize: '14px' }}>Loading…</p>;
  if (!broadcasts?.length) return <p style={{ color: '#666', fontSize: '14px' }}>No broadcasts sent yet.</p>;

  return (
    <div className="card" style={{ padding: 0 }}>
      {broadcasts.map((b, i) => {
        const nonOpeners = b.sent - b.opened;
        return (
          <div key={b.id} style={{
            padding: '16px 20px',
            borderBottom: i < broadcasts.length - 1 ? '1px solid #f3f4f6' : 'none',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
          }}>
            <div style={{ minWidth: '200px' }}>
              <div style={{ fontWeight: '600', fontSize: '15px', color: '#1a1a1a' }}>{b.subject}</div>
              <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>
                {new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {b.resend_of && ' • resend to non-openers'}
                {' • '}{LIST_LABELS[b.list_target]?.toLowerCase() || b.list_target}
                {!TAGGED_LIST_TARGETS.includes(b.list_target) && <> — {AUDIENCE_LABELS[b.audience]?.toLowerCase() || b.audience}</>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                  <Mail size={14} /> {b.sent}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>sent</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                  <MailOpen size={14} /> {b.opened} <span style={{ color: '#999', fontWeight: 'normal' }}>({b.open_rate}%)</span>
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>opened</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setResendTarget(b)} disabled={b.sent === 0 || nonOpeners === 0}>
                Resend to non-openers
              </Button>
            </div>
          </div>
        );
      })}

      {resendTarget && (
        <ResendDialog
          broadcast={resendTarget}
          onClose={() => setResendTarget(null)}
          onSent={() => { setResendTarget(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── SMTP2GO Non-Openers Tab ──────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function NonOpenersTab() {
  const [subject, setSubject] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [excludeEmails, setExcludeEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!subject.trim()) { toast.error('Enter the campaign subject line'); return; }
    setLoading(true);
    setResult(null);
    try {
      const resp = await axios.post('/admin/smtp2go/non-openers', {
        subject: subject.trim(),
        start_date: startDate,
        end_date: endDate,
        exclude_emails: excludeEmails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean),
      });
      setResult(resp.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pull activity from SMTP2GO');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    const csv = ['email', ...result.emails].join('\n') + '\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `non-openers-${result.subject.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card" style={{ maxWidth: '640px' }}>
      <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
        Pulls every Delivered and Opened event for a campaign straight from SMTP2GO (all pages), and lists who
        received it but never opened. Calendar purchasers, [TEST] sends, and your sender/notification addresses
        are left out. Opens are counted up to today, even if they came after the send dates. SMTP2GO only keeps
        about 30 days of history.
      </p>
      <div className="form-group">
        <label>Exact subject line</label>
        <input type="text" className="form-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: '1 1 160px' }}>
          <label>Sent from</label>
          <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: '1 1 160px' }}>
          <label>Sent through</label>
          <input type="date" className="form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label>Other addresses to leave out (optional)</label>
        <input type="text" className="form-input" placeholder="you@example.com, other@example.com"
          value={excludeEmails} onChange={(e) => setExcludeEmails(e.target.value)} />
      </div>
      <Button onClick={run} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <RefreshCw size={16} /> {loading ? 'Pulling from SMTP2GO…' : 'Find non-openers'}
      </Button>

      {result && (
        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ fontWeight: '600', fontSize: '15px', color: '#1a1a1a' }}>{result.summary}</div>
          <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
            Also left out: {result.excluded_purchasers} calendar purchaser{result.excluded_purchasers === 1 ? '' : 's'},{' '}
            {result.excluded_own} of your own address{result.excluded_own === 1 ? '' : 'es'}.
          </div>
          {result.non_openers > 0 && (
            <Button variant="outline" onClick={downloadCsv} style={{ marginTop: '12px' }}>
              Download CSV ({result.non_openers})
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Legacy Cleanup Tab ──────────────────────────────────────────────────────

const DEFAULT_RECONFIRM_SUBJECT = 'Still want to hear from the OBX Beach Bum?';
const DEFAULT_RECONFIRM_MESSAGE = `A while back you joined my email list — maybe on my old website, or when you picked up something from my shop.

These days I'm sharing Outer Banks photography and stories at theobxbeachbum.com, and I'd love to keep sending them your way. If you stay, you can pick a short roundup every Monday or every new story as it's published.

I'm tidying up my list so I only email folks who actually want to hear from me. Want to stay?`;

function daysFromTodayISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 110px', background: '#f9fafb', borderRadius: '8px', padding: '12px 14px' }}>
      <div style={{ fontSize: '22px', fontWeight: '700', color: color || '#1a1a1a' }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#666' }}>{label}</div>
    </div>
  );
}

function LegacyCleanupTab() {
  const [overview, setOverview] = useState(null);
  const [subject, setSubject] = useState(DEFAULT_RECONFIRM_SUBJECT);
  const [deadline, setDeadline] = useState(daysFromTodayISO(21));
  const [message, setMessage] = useState(DEFAULT_RECONFIRM_MESSAGE);
  const [imageUrl, setImageUrl] = useState('');
  const [keepLabel, setKeepLabel] = useState('');
  const [removeLabel, setRemoveLabel] = useState('');
  const [greeting, setGreeting] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [batchSize, setBatchSize] = useState(200);
  const [busy, setBusy] = useState('');
  const imageInputRef = useRef(null);

  const load = async () => {
    try {
      const resp = await axios.get('/admin/reconfirm');
      setOverview(resp.data);
      if (resp.data.campaign) {
        setSubject(resp.data.campaign.subject);
        setDeadline(resp.data.campaign.deadline);
        setMessage(resp.data.campaign.message);
        setImageUrl(resp.data.campaign.image_url || '');
        setKeepLabel(resp.data.campaign.keep_label || '');
        setRemoveLabel(resp.data.campaign.remove_label || '');
        setGreeting(resp.data.campaign.greeting !== false);
      }
    } catch (error) {
      toast.error('Failed to load the legacy cleanup');
    }
  };

  useEffect(() => { load(); }, []);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy('');
      load();
    }
  };

  const saveCampaign = () => run('save', async () => {
    const resp = await axios.post('/admin/reconfirm/campaign', {
      subject: subject.trim(), deadline, message,
      image_url: imageUrl, keep_label: keepLabel, remove_label: removeLabel, greeting,
    });
    toast.success(resp.data.message);
  });

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large. Maximum size: 10MB'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post('/upload/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.url) { setImageUrl(response.data.url); toast.success('Image uploaded — click Save changes to use it'); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const sendTest = () => run('test', async () => {
    if (!testEmail.trim()) { toast.error('Enter an address for the test'); return; }
    const resp = await axios.post('/admin/reconfirm/test', { email: testEmail.trim() });
    toast.success(resp.data.message);
  });

  const sendBatch = (reminder) => run(reminder ? 'reminder' : 'send', async () => {
    const what = reminder ? 'a reminder' : 'the first email';
    if (!window.confirm(`Send ${what} to the next ${batchSize} pending legacy subscribers?`)) return;
    const resp = await axios.post('/admin/reconfirm/send', { batch_size: Number(batchSize), reminder });
    const { sent, failed, remaining } = resp.data;
    toast.success(`Sent ${sent}${failed ? `, ${failed} failed` : ''}. ${remaining} still to go.`);
  });

  const checkBounces = () => run('bounces', async () => {
    const resp = await axios.post('/admin/reconfirm/check-bounces');
    const { hard_bounced, spam, removed } = resp.data;
    toast.success(`SMTP2GO: ${hard_bounced} hard bounce(s), ${spam} spam complaint(s). ${removed} taken off the list.`);
  });

  const downloadPending = () => run('csv', async () => {
    const resp = await axios.get('/admin/reconfirm/pending.csv', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'legacy-pending.csv';
    a.click();
    URL.revokeObjectURL(url);
  });

  const purge = () => run('purge', async () => {
    const pending = overview?.counts.pending || 0;
    if (!window.confirm(`Take all ${pending} people who never answered off the list? They'll be marked unsubscribed.`)) return;
    const resp = await axios.post('/admin/reconfirm/purge');
    toast.success(`${resp.data.purged} removed from the list.`);
  });

  if (!overview) return <div className="loading-spinner"></div>;
  const { campaign, counts, purge_allowed: purgeAllowed } = overview;
  const sectionStyle = { marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' };
  const hintStyle = { fontSize: '13px', color: '#888', margin: '6px 0 0' };

  return (
    <div className="card" style={{ maxWidth: '720px' }}>
      {campaign && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <StatBox label="Kept (moved to Site)" value={counts.kept} color="#166534" />
          <StatBox label="Removed" value={counts.removed} />
          <StatBox label="Bounced / spam" value={counts.bounced} />
          <StatBox label="No answer yet" value={counts.pending} color="#b45309" />
          {counts.purged > 0 && <StatBox label="Purged" value={counts.purged} />}
        </div>
      )}

      <div className="form-group">
        <label>Subject</label>
        <input type="text" className="form-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Answer by</label>
        <input type="date" className="form-input" style={{ maxWidth: '200px' }} value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        <p style={hintStyle}>Shown in the email. You can purge non-answers the day after.</p>
      </div>
      <div className="form-group">
        <label>Picture (optional)</label>
        <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadImage} />
        {imageUrl ? (
          <div style={{ position: 'relative', maxWidth: '320px' }}>
            <img src={imageUrl} alt="" style={{ width: '100%', borderRadius: '6px', display: 'block' }} />
            <button type="button" onClick={() => setImageUrl('')} title="Remove picture"
              style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <Button variant="outline" onClick={() => imageInputRef.current?.click()} disabled={uploading}>
            <ImagePlus size={16} style={{ marginRight: '6px' }} />
            {uploading ? 'Uploading…' : 'Add a picture'}
          </Button>
        )}
        <p style={hintStyle}>Shown full width under the logo, above the message.</p>
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal', cursor: 'pointer' }}>
          <input type="checkbox" checked={greeting} onChange={(e) => setGreeting(e.target.checked)} />
          Start with "Hi [first name],"
        </label>
      </div>
      <div className="form-group">
        <label>Message</label>
        <textarea className="form-input" rows={14} value={message} onChange={(e) => setMessage(e.target.value)} />
        <p style={hintStyle}>
          Leave a blank line between paragraphs. Put [BUTTONS] on a line by itself where the buttons go — without it
          they go after the message. The email then adds the answer-by date, a line saying replying works too, and an
          unsubscribe link.
        </p>
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: '1 1 220px' }}>
          <label>Keep button says</label>
          <input type="text" className="form-input" placeholder="Keep me" value={keepLabel} onChange={(e) => setKeepLabel(e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: '1 1 220px' }}>
          <label>Remove button says</label>
          <input type="text" className="form-input" placeholder="Remove me" value={removeLabel} onChange={(e) => setRemoveLabel(e.target.value)} />
        </div>
      </div>
      <Button onClick={saveCampaign} disabled={!!busy}>
        {busy === 'save' ? 'Saving…' : campaign ? 'Save changes' : 'Start campaign'}
      </Button>
      <p style={hintStyle}>
        {campaign
          ? counts.not_enrolled > 0
            ? `${counts.not_enrolled} legacy-only subscriber(s) aren't in the campaign yet — saving adds them.`
            : 'Everyone on the Legacy list (only) is in the campaign.'
          : 'Starting enrolls everyone whose only list is Legacy. Nothing is emailed yet. People also on Site, Beach Bum Society or Customers are left alone.'}
      </p>

      {campaign && (
        <>
          <div style={sectionStyle}>
            <label style={{ fontWeight: '600' }}>Send a test</label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
              <input type="email" className="form-input" style={{ flex: '1 1 240px' }} placeholder="you@example.com"
                value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
              <Button variant="outline" onClick={sendTest} disabled={!!busy}>
                {busy === 'test' ? 'Sending…' : 'Send test'}
              </Button>
            </div>
            <p style={hintStyle}>The test's buttons open a preview of the real pages marked "Test preview" — you can click through, but nothing is saved.</p>
          </div>

          <div style={sectionStyle}>
            <label style={{ fontWeight: '600' }}>Send in batches</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '14px' }}>Batch size</span>
              <input type="number" min="1" max="1000" className="form-input" style={{ width: '100px' }}
                value={batchSize} onChange={(e) => setBatchSize(e.target.value)} />
              <Button onClick={() => sendBatch(false)} disabled={!!busy || counts.never_emailed === 0}>
                <Send size={16} style={{ marginRight: '6px' }} />
                {busy === 'send' ? 'Sending…' : `Send first email (${counts.never_emailed} left)`}
              </Button>
              <Button variant="outline" onClick={() => sendBatch(true)} disabled={!!busy || counts.reminder_ready === 0}>
                {busy === 'reminder' ? 'Sending…' : `Send reminder (${counts.reminder_ready} ready)`}
              </Button>
            </div>
            <p style={hintStyle}>
              Reminders only go to people who got the first email at least 3 days ago and haven't answered — each person
              gets at most one. After a batch, use Check bounces before sending the next.
            </p>
          </div>

          <div style={sectionStyle}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button variant="outline" onClick={checkBounces} disabled={!!busy}>
                <RefreshCw size={16} style={{ marginRight: '6px' }} />
                {busy === 'bounces' ? 'Checking SMTP2GO…' : 'Check bounces'}
              </Button>
              <Button variant="outline" onClick={downloadPending} disabled={!!busy || counts.pending === 0}>
                Download no-answer list (CSV)
              </Button>
            </div>
            <p style={hintStyle}>
              Check bounces takes hard-bounced and spam-complaint addresses off the list. Replies come to your inbox —
              mark those people by hand on the Subscribers page.
            </p>
          </div>

          <div style={sectionStyle}>
            <Button
              onClick={purge}
              disabled={!!busy || !purgeAllowed || counts.pending === 0}
              style={{ background: purgeAllowed ? '#b91c1c' : undefined }}
            >
              {busy === 'purge' ? 'Removing…' : `Remove everyone who didn't answer (${counts.pending})`}
            </Button>
            <p style={hintStyle}>
              {purgeAllowed
                ? 'The deadline has passed. This marks them unsubscribed (records are kept, so an old CSV import won\'t add them back).'
                : `Available the day after ${campaign.deadline}.`}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Automatic Sends Tab ─────────────────────────────────────────────────────

const AUTO_SEND_LABELS = {
  daily_obx: 'Daily OBX (6am)',
  daily_post_digest: 'New posts (7am)',
  weekly_digest: 'Weekly roundup (Mon 8am)',
};

const AUTO_SEND_STATUS = {
  done: { label: 'Sent', color: '#166534' },
  running: { label: 'Sending…', color: '#1d4ed8' },
  skipped: { label: 'Nothing to send', color: '#6b7280' },
  interrupted: { label: 'Cut off', color: '#b91c1c' },
};

function formatRunDate(runKey) {
  const [y, m, d] = runKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function AutoSendsTab() {
  const [runs, setRuns] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const resp = await axios.get('/admin/auto-sends');
      setRuns(resp.data.runs);
    } catch (error) {
      toast.error('Failed to load automatic sends');
    }
  };

  useEffect(() => { load(); }, []);

  // While something is sending, refresh every few seconds to show progress.
  const anyRunning = (runs || []).some((r) => r.status === 'running');
  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [anyRunning]);

  const checkFinish = async () => {
    setBusy('check');
    try {
      const resp = await axios.get('/admin/auto-sends/finish-daily');
      setPlan(resp.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check failed');
    } finally {
      setBusy('');
    }
  };

  const finish = async () => {
    if (!window.confirm(`Send this morning's email to ${plan.to_send} daily reader(s)?`)) return;
    setBusy('finish');
    try {
      const resp = await axios.post('/admin/auto-sends/finish-daily');
      toast.success(resp.data.message);
      setPlan(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Send failed');
    } finally {
      setBusy('');
    }
  };

  if (!runs) return <div className="loading-spinner"></div>;
  const hintStyle = { fontSize: '13px', color: '#888', margin: '6px 0 0' };
  const cell = { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', verticalAlign: 'top' };

  return (
    <>
      <div className="card" style={{ maxWidth: '720px', marginBottom: '20px' }}>
        <label style={{ fontWeight: '600' }}>Finish a cut-off 7am email</label>
        <p style={hintStyle}>
          For a morning the new-post email stopped partway. Check first: it asks SMTP2GO who already got it, and only
          the rest are emailed.
        </p>
        <div style={{ marginTop: '10px' }}>
          <Button variant="outline" onClick={checkFinish} disabled={!!busy}>
            {busy === 'check' ? 'Checking SMTP2GO…' : "Check this morning's email"}
          </Button>
        </div>
        {plan && !plan.ready && <p style={{ marginTop: '12px' }}>{plan.reason}</p>}
        {plan && plan.ready && (
          <div style={{ marginTop: '14px', fontSize: '14px' }}>
            <p style={{ margin: '0 0 6px' }}><strong>Subject:</strong> {plan.subject}</p>
            <p style={{ margin: '0 0 10px' }}><strong>Posts:</strong> {plan.posts.join(', ')}</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <StatBox label="Daily readers" value={plan.daily_total} />
              <StatBox label="Already got it" value={plan.already_received} color="#166534" />
              <StatBox label="Still to send" value={plan.to_send} color="#b45309" />
            </div>
            {plan.already_received === 0 && (
              <p style={{ color: '#b91c1c', margin: '0 0 10px' }}>
                SMTP2GO shows nobody got this subject today. If some people did, don't send — the subject may not match.
              </p>
            )}
            <Button onClick={finish} disabled={!!busy || plan.to_send === 0}>
              <Send size={16} style={{ marginRight: '6px' }} />
              {busy === 'finish' ? 'Starting…' : `Send to the ${plan.to_send} who didn't get it`}
            </Button>
          </div>
        )}
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        {runs.length === 0 ? (
          <p style={{ color: '#666' }}>Nothing yet. Runs are recorded from now on, starting with the next automatic email.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', fontSize: '13px', color: '#666' }}>
                <th style={cell}>Day</th>
                <th style={cell}>Email</th>
                <th style={cell}>Sent</th>
                <th style={cell}>Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const status = AUTO_SEND_STATUS[run.status] || { label: run.status, color: '#374151' };
                const sent = run.status === 'done' ? run.sent : run.sent_so_far;
                return (
                  <tr key={`${run.kind}-${run.run_key}`}>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{formatRunDate(run.run_key)}</td>
                    <td style={cell}>
                      {AUTO_SEND_LABELS[run.kind] || run.kind}
                      {run.subject && <div style={{ fontSize: '12px', color: '#888' }}>{run.subject}</div>}
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {run.status === 'skipped' ? '—' : `${sent} of ${run.total}`}
                      {run.failed > 0 && (
                        <div style={{ fontSize: '12px', color: '#b91c1c' }} title={(run.errors || []).join('\n')}>
                          {run.failed} failed
                        </div>
                      )}
                    </td>
                    <td style={cell}>
                      <span style={{ color: status.color, fontWeight: '600' }}>{status.label}</span>
                      {run.note && <div style={{ fontSize: '12px', color: '#888' }}>{run.note}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PAGE_TABS = {
  broadcast: {
    label: 'Broadcast',
    title: 'Send Broadcast',
    blurb: 'Send a one-off email to subscribers without needing a blog post.',
    Component: BroadcastTab,
  },
  digest: {
    label: 'Weekly Digest',
    title: 'Weekly Digest',
    blurb: 'Send a digest of this week\'s posts to subscribers.',
    Component: DigestTab,
  },
  history: {
    label: 'Sent Broadcasts',
    title: 'Sent Broadcasts',
    blurb: "Open counts come from a tracking pixel, so they undercount — some inboxes block images by default. Treat \"opened\" as a floor, not an exact count.",
    Component: HistoryTab,
  },
  nonOpeners: {
    label: 'SMTP2GO Non-Openers',
    title: 'SMTP2GO Non-Openers',
    blurb: 'Build a non-opener list from SMTP2GO\'s own delivery and open events, for any campaign sent through it.',
    Component: NonOpenersTab,
  },
  legacyCleanup: {
    label: 'Legacy Cleanup',
    title: 'Legacy List Cleanup',
    blurb: 'Ask legacy subscribers to confirm they want to stay. "Keep me" moves them onto the Site list; anyone who doesn\'t answer by the deadline can be removed.',
    Component: LegacyCleanupTab,
  },
  autoSends: {
    label: 'Automatic Sends',
    title: 'Automatic Sends',
    blurb: 'Every run of the emails that go out on their own: Daily OBX at 6am, new posts at 7am, and the weekly roundup Mondays at 8am. If a restart cuts one off, it finishes on its own a few minutes later without repeating anyone.',
    Component: AutoSendsTab,
  },
};

function Broadcast({ onLogout }) {
  const [tab, setTab] = useState('broadcast');

  const tabStyle = (active) => ({
    padding: '8px 20px',
    borderRadius: '20px',
    border: 'none',
    background: active ? '#1a1a1a' : '#e5e7eb',
    color: active ? 'white' : '#374151',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
  });

  return (
    <AdminLayout onLogout={onLogout} currentPage="broadcast">
      <div data-testid="broadcast-page">
        <div className="page-header">
          <h1>{PAGE_TABS[tab].title}</h1>
          <p style={{ color: '#666', marginTop: '4px' }}>{PAGE_TABS[tab].blurb}</p>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {Object.entries(PAGE_TABS).map(([id, { label }]) => (
            <button key={id} style={tabStyle(tab === id)} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        {(() => { const { Component } = PAGE_TABS[tab]; return <Component />; })()}
      </div>
    </AdminLayout>
  );
}

export default Broadcast;
